import { curSym } from "@/lib/company";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Feedback from "@/pages/Feedback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Star, MessageSquareText, Loader2, Phone, User2, Receipt, BarChart3, ListChecks,
  Plus, Pencil, Trash2, X, MapPin, Ticket, Percent, Lightbulb,
} from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend } from "recharts";
import { fmtTL } from "@/components/StatusBadge";

const StarRow = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`h-3.5 w-3.5 ${n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
    ))}
  </div>
);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; } };
const Q_EMPTY = { text: "", type: "rating", options: [], active: true };

export default function Surveys() {
  const { can } = useAuth();
  const canManage = can("survey.manage");
  const canSurvey = can("survey.read");
  const canFeedback = can("report.read");
  const [sp] = useSearchParams();
  const tabDefs = [
    ...(canSurvey ? [["list", "Anketler", MessageSquareText], ["analytics", "Analiz", BarChart3], ["coupons", "Kuponlar", Percent]] : []),
    ...(canFeedback ? [["feedback", "Öneri / Şikayet", Lightbulb]] : []),
    ...(canManage ? [["questions", "Sorular", ListChecks], ["campaign", "Kampanya", Ticket]] : []),
  ];
  const [tab, setTab] = useState(sp.get("tab") || (canSurvey ? "list" : "feedback"));
  const [data, setData] = useState({ items: [], count: 0, avg: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [coupon, setCoupon] = useState(null);
  const [coupons, setCoupons] = useState({ items: [], count: 0 });
  const [couponStats, setCouponStats] = useState(null);
  const [coupStatus, setCoupStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const [qOpen, setQOpen] = useState(false);
  const [qEditing, setQEditing] = useState(null);
  const [qForm, setQForm] = useState(Q_EMPTY);
  const [optInput, setOptInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/surveys").then((r) => setData(r.data)).catch(() => {}),
      api.get("/surveys/analytics").then((r) => setAnalytics(r.data)).catch(() => {}),
      canManage ? api.get("/surveys/questions").then((r) => setQuestions(r.data.items || [])).catch(() => {}) : Promise.resolve(),
      api.get("/settings/survey-coupon").then((r) => setCoupon(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [canManage]);
  useEffect(() => { load(); }, [load]);

  const saveCoupon = async () => {
    try { const { data } = await api.put("/settings/survey-coupon", coupon); setCoupon(data); toast.success("Kampanya ayarı kaydedildi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const loadCoupons = useCallback(() => {
    api.get(`/coupons?status=${coupStatus}`).then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/coupons/analytics").then((r) => setCouponStats(r.data)).catch(() => {});
  }, [coupStatus]);
  useEffect(() => { if (tab === "coupons") loadCoupons(); }, [tab, loadCoupons]);


  const openQ = (q) => {
    setQEditing(q || null);
    setQForm(q ? { text: q.text, type: q.type || "rating", options: q.options || [], active: q.active !== false } : Q_EMPTY);
    setOptInput(""); setQOpen(true);
  };
  const addOpt = () => {
    const v = optInput.trim();
    if (v && !qForm.options.includes(v)) setQForm((f) => ({ ...f, options: [...f.options, v] }));
    setOptInput("");
  };
  const submitQ = async () => {
    if (!qForm.text.trim()) return toast.error("Soru metni zorunlu");
    if (qForm.type === "choice" && qForm.options.length < 2) return toast.error("Çoktan seçmeli için en az 2 seçenek ekleyin");
    setSaving(true);
    const body = { text: qForm.text.trim(), type: qForm.type, options: qForm.type === "choice" ? qForm.options : [], active: qForm.active };
    try {
      if (qEditing) await api.put(`/surveys/questions/${qEditing.id}`, body);
      else await api.post("/surveys/questions", body);
      toast.success(qEditing ? "Soru güncellendi" : "Soru eklendi");
      setQOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const delQ = async (q) => {
    if (!window.confirm(`"${q.text}" sorusu silinsin mi?`)) return;
    try { await api.delete(`/surveys/questions/${q.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 fade-in" data-testid="surveys-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
          <MessageSquareText className="h-6 w-6 text-amber-500" /> Anketler & Öneriler
        </h1>
        <p className="mt-1 text-sm text-slate-500">Müşteri anketleri, analizler ve öneri/şikayet penceresi tek yerde.</p>
      </div>

      {canSurvey && (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="survey-stat-count">
          <p className="overline">Toplam Anket</p>
          <p className="mt-1 font-heading text-3xl font-black text-slate-900">{data.count}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 shadow-sm" data-testid="survey-stat-avg">
          <p className="overline">Ortalama Puan</p>
          <div className="mt-1 flex items-center gap-2">
            <p className="font-heading text-3xl font-black text-amber-600">{data.avg?.toFixed?.(2) ?? data.avg}</p>
            <StarRow value={Math.round(data.avg)} />
          </div>
        </div>
      </div>
      )}

      <div className="inline-flex flex-wrap rounded-lg bg-slate-100 p-1">
        {tabDefs.map(([k, l, Icon]) => (
          <button key={k} data-testid={`survey-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "feedback" && canFeedback && <Feedback />}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>
      ) : (
        <>
          {tab === "list" && (
            <div className="space-y-3">
              {data.items.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="surveys-empty">
                  Henüz müşteri anketi yok. Fiş üzerindeki karekod tarandığında burada listelenir.
                </div>
              )}
              {data.items.map((s) => (
                <div key={s.id} data-testid={`survey-card-${s.id}`} className={`rounded-2xl border bg-white p-4 shadow-sm ${s.rating <= 2 ? "border-red-200" : "border-slate-200"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <StarRow value={s.rating} />
                      <span className={`text-sm font-bold ${s.rating <= 2 ? "text-red-600" : "text-slate-800"}`}>{s.rating}/5</span>
                      {s.rating <= 2 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600">DÜŞÜK</span>}
                    </div>
                    <span className="text-xs text-slate-400">{fmtDate(s.created_at)}</span>
                  </div>
                  {s.comment && <p className="mt-2 text-sm text-slate-700">“{s.comment}”</p>}
                  {(s.answers || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.answers.map((a, i) => (
                        <span key={i} className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200">
                          {a.question_text}: <b>{a.rating ? `${a.rating}★` : a.choice || "-"}</b>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    {s.name ? <span className="flex items-center gap-1"><User2 className="h-3 w-3" /> {s.name}</span> : null}
                    {s.phone ? <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</span> : null}
                    {s.receipt_no ? <span className="flex items-center gap-1"><Receipt className="h-3 w-3" /> {s.receipt_no}</span> : null}
                    {s.location_name ? <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location_name}</span> : null}
                    {s.coupon_status === "issued" && s.coupon_code ? <span data-testid={`survey-coupon-issued-${s.id}`} className="flex items-center gap-1 font-semibold text-emerald-600"><Ticket className="h-3 w-3" /> Kupon: {s.coupon_code}</span> : null}
                    {s.coupon_status === "suppressed_24h" ? <span data-testid={`survey-coupon-suppressed-${s.id}`} className="flex items-center gap-1 font-semibold text-amber-600" title="Aynı numaradan son 24 saatte zaten kupon üretildi (mükerrer engellendi)"><Ticket className="h-3 w-3" /> Kupon verilmedi · 24s mükerrer</span> : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "analytics" && analytics && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2" data-testid="survey-trend-chart">
                <p className="overline mb-3">Aylık Ortalama Puan (son 6 ay)</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={analytics.trend} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                    <Line type="monotone" dataKey="avg" name="Ort. Puan" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="survey-location-chart">
                <p className="overline mb-3">Lokasyon Bazlı Kırılım</p>
                {analytics.by_location.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-400">Fiş/lokasyon eşleşen anket yok.</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.by_location.map((l) => (
                      <div key={l.location} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                        <span className="truncate text-slate-700">{l.location}</span>
                        <span className="flex items-center gap-2"><StarRow value={Math.round(l.avg)} /><b className="tabular text-amber-600">{l.avg}</b><span className="text-[11px] text-slate-400">({l.count})</span></span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="survey-distribution-chart">
                <p className="overline mb-3">Puan Dağılımı</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={analytics.distribution} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="stars" tickFormatter={(v) => `${v}★`} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                    <Bar dataKey="count" name="Anket" radius={[4, 4, 0, 0]}>
                      {analytics.distribution.map((d) => (
                        <Cell key={d.stars} fill={d.stars <= 2 ? "#ef4444" : d.stars === 3 ? "#f59e0b" : "#10b981"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {tab === "questions" && canManage && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="survey-questions">
              <div className="mb-3 flex items-center justify-between">
                <p className="overline">Anket Soruları</p>
                <Button data-testid="question-new-btn" onClick={() => openQ(null)} size="sm" className="h-8 bg-brand-green text-white hover:bg-brand-green-600">
                  <Plus className="mr-1 h-4 w-4" /> Yeni Soru
                </Button>
              </div>
              <div className="space-y-2">
                {questions.length === 0 && <p className="text-sm text-slate-400">Tanımlı soru yok.</p>}
                {questions.map((q) => (
                  <div key={q.id} data-testid={`question-card-${q.id}`} className={`flex items-start justify-between gap-3 rounded-xl border p-3 ${q.active === false ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200"}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{q.text}</p>
                      <p className="mt-0.5 text-[11px] text-slate-400">
                        {q.type === "rating" ? "Yıldız (1-5)" : `Çoktan seçmeli: ${(q.options || []).join(" · ")}`}{q.active === false ? " · pasif" : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button data-testid={`question-edit-${q.id}`} onClick={() => openQ(q)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`question-del-${q.id}`} onClick={() => delQ(q)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "coupons" && (
            <div className="space-y-4" data-testid="coupons-panel">
              {couponStats && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="overline">Toplam Kupon</p><p data-testid="coup-total" className="font-heading text-2xl font-bold text-slate-900">{couponStats.total}</p></div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"><p className="overline">Aktif</p><p data-testid="coup-active" className="font-heading text-2xl font-bold text-emerald-600">{couponStats.active}</p></div>
                  <div className="rounded-xl border border-brand-green/30 bg-brand-green/10 p-4"><p className="overline">Kullanıldı</p><p data-testid="coup-used" className="font-heading text-2xl font-bold text-brand-green">{couponStats.used}</p></div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><p className="overline">Süresi Doldu</p><p data-testid="coup-expired" className="font-heading text-2xl font-bold text-slate-500">{couponStats.expired}</p></div>
                </div>
              )}
              {couponStats && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4"><p className="overline">Dönüşüm Oranı</p><p data-testid="coup-conversion" className="font-heading text-2xl font-bold text-brand-orange">%{couponStats.conversion_rate}</p><p className="text-[11px] text-slate-500">Anket kuponu: %{couponStats.survey_conversion_rate} ({couponStats.survey_used}/{couponStats.survey_total})</p></div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="overline">Ciroya Dönüşen</p><p data-testid="coup-redeemed" className="font-heading text-2xl font-bold text-slate-900">{fmtTL(couponStats.redeemed_value)}</p><p className="text-[11px] text-slate-500">Kullanılan kuponların satış toplamı</p></div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="overline">Anket Kaynaklı</p><p className="font-heading text-2xl font-bold text-slate-900">{couponStats.survey_total}</p><p className="text-[11px] text-slate-500">ankete bağlı üretilen kupon</p></div>
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="mb-3 font-heading font-bold text-slate-800">Aylık Üretim vs Kullanım (son 6 ay)</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={couponStats?.monthly || []} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Legend />
                    <Line type="monotone" dataKey="issued" name="Üretilen" stroke="#f59e0b" strokeWidth={2} />
                    <Line type="monotone" dataKey="used" name="Kullanılan" stroke="#16a34a" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="coupons-list">
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {[["all", "Tümü"], ["active", "Aktif"], ["used", "Kullanıldı"], ["expired", "Süresi Doldu"]].map(([v, l]) => (
                    <button key={v} data-testid={`coup-filter-${v}`} onClick={() => setCoupStatus(v)}
                      className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${coupStatus === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
                  ))}
                </div>
                {coupons.items.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-400">Kupon yok.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400">
                        <th className="py-2">Kod</th><th>Müşteri</th><th>İndirim</th><th>Durum</th><th>Kullanım</th><th>Geçerlilik</th>
                      </tr></thead>
                      <tbody>
                        {coupons.items.map((c) => (
                          <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="border-b border-slate-50">
                            <td className="py-2 font-mono font-bold text-slate-800">{c.code}</td>
                            <td className="text-slate-600">{c.name || "—"}<br /><span className="text-[11px] text-slate-400">{c.phone || ""}</span></td>
                            <td className="text-slate-600">{c.discount_type === "amount" ? fmtTL(c.discount_value) : `%${c.discount_value}`}{c.min_purchase > 0 ? <span className="text-[11px] text-slate-400"> · min {fmtTL(c.min_purchase)}</span> : null}</td>
                            <td>
                              {c.status === "used" && <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[11px] font-semibold text-brand-green">Kullanıldı</span>}
                              {c.status === "active" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">Aktif</span>}
                              {c.status === "expired" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Süresi Doldu</span>}
                            </td>
                            <td className="text-[11px] text-slate-500">{c.used_sale_no || "—"}</td>
                            <td className="text-[11px] text-slate-500">{c.expires_at ? fmtDate(c.expires_at) : "süresiz"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "campaign" && canManage && coupon && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="survey-campaign">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="font-heading text-lg font-bold text-slate-900">Anket İndirim Kuponu</p>
                  <p className="text-sm text-slate-500">Anketi tamamlayan (ad+telefon doğrulanan) müşteriye bir sonraki alışverişinde geçerli tek kullanımlık kod verilir. Kasada girilir.</p>
                </div>
                <Switch data-testid="campaign-enabled" checked={coupon.enabled} onCheckedChange={(v) => setCoupon({ ...coupon, enabled: v })} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>İndirim Tipi</Label>
                  <div className="mt-1 flex gap-2">
                    {[["percent", "Yüzde (%)"], ["amount", `Tutar (${curSym()})`]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`campaign-type-${v}`} onClick={() => setCoupon({ ...coupon, discount_type: v })}
                        className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${coupon.discount_type === v ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-slate-300 text-slate-600"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div><Label>İndirim Değeri {coupon.discount_type === "percent" ? "(%)" : `(${curSym()})`}</Label>
                  <Input data-testid="campaign-value" type="number" value={coupon.discount_value} onChange={(e) => setCoupon({ ...coupon, discount_value: +e.target.value })} className="mt-1" /></div>
                <div><Label>Geçerlilik (gün)</Label>
                  <Input data-testid="campaign-days" type="number" value={coupon.valid_days} onChange={(e) => setCoupon({ ...coupon, valid_days: +e.target.value })} className="mt-1" /></div>
                <div><Label>Min. Alışveriş ({curSym()})</Label>
                  <Input data-testid="campaign-min" type="number" value={coupon.min_purchase} onChange={(e) => setCoupon({ ...coupon, min_purchase: +e.target.value })} className="mt-1" /></div>
              </div>
              <div className="mt-4 rounded-lg bg-brand-orange/5 p-3 ring-1 ring-inset ring-brand-orange/20">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="mb-0 font-semibold">Kupon üretilince müşteriye SMS gönder</Label>
                    <p className="text-[11px] text-slate-500">Anketi dolduran müşteriye kupon kodu SMS ile iletilir (ana SMS anahtarı açıksa).</p>
                  </div>
                  <Switch data-testid="campaign-coupon-sms" checked={coupon.coupon_sms !== false} onCheckedChange={(v) => setCoupon({ ...coupon, coupon_sms: v })} />
                </div>
                {coupon.coupon_sms !== false && (
                  <div className="mt-3">
                    <Label>SMS metni</Label>
                    <textarea data-testid="campaign-coupon-sms-msg" value={coupon.coupon_sms_message || ""} onChange={(e) => setCoupon({ ...coupon, coupon_sms_message: e.target.value })}
                      rows={2} maxLength={200} className="mt-1 w-full rounded-md border border-input bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange" />
                    <p className="mt-1 text-[11px] text-slate-400">Değişkenler: <code>{"{code}"}</code> kupon kodu · <code>{"{date}"}</code> son geçerlilik · <code>{"{sender}"}</code> gönderen adı. Türkçe karakter kullanmayın.</p>
                  </div>
                )}
              </div>
              <Button data-testid="campaign-save" onClick={saveCoupon} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">Kaydet</Button>
            </div>
          )}
        </>
      )}

      <Dialog open={qOpen} onOpenChange={setQOpen}>
        <DialogContent className="max-w-md" data-testid="question-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{qEditing ? "Soruyu Düzenle" : "Yeni Anket Sorusu"}</DialogTitle>
            <DialogDescription>Müşterilere anket sayfasında gösterilecek soruyu tanımlayın.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Soru Metni</Label>
              <Input data-testid="question-text" value={qForm.text} onChange={(e) => setQForm({ ...qForm, text: e.target.value })} className="mt-1" placeholder="ör. Çalışanlarımız size nasıl davrandı?" /></div>
            <div><Label>Cevap Tipi</Label>
              <div className="mt-1 flex gap-2">
                {[["rating", "Yıldız (1-5)"], ["choice", "Çoktan seçmeli"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`question-type-${v}`} onClick={() => setQForm({ ...qForm, type: v })}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${qForm.type === v ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-slate-300 text-slate-600"}`}>{l}</button>
                ))}
              </div>
            </div>
            {qForm.type === "choice" && (
              <div>
                <Label>Seçenekler</Label>
                <div className="mt-1 flex gap-2">
                  <Input data-testid="question-opt-input" value={optInput} onChange={(e) => setOptInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOpt(); } }} placeholder="Seçenek yazıp Ekle'ye basın" />
                  <Button type="button" data-testid="question-opt-add" variant="outline" onClick={addOpt}>Ekle</Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {qForm.options.map((o) => (
                    <span key={o} className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                      {o}
                      <button type="button" onClick={() => setQForm((f) => ({ ...f, options: f.options.filter((x) => x !== o) }))} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
              <Label className="mb-0">Aktif (ankette göster)</Label>
              <Switch data-testid="question-active" checked={qForm.active} onCheckedChange={(v) => setQForm({ ...qForm, active: v })} />
            </div>
            <Button data-testid="question-submit" onClick={submitQ} disabled={saving} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {qEditing ? "Kaydet" : "Ekle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
